// pages/Projects.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar } from 'lucide-react';

const Projects = () => {
  const [filter, setFilter] = useState('all');

  const projects = [
    {
      id: 'mv-switchgear-installation',
      title: 'MV Switchgear Installation',
      location: 'Nairobi, Kenya',
      image: '/images/projects/mv-switchgear-nairobi.jpg',
      year: '2024',
      scope: '11kV Switchgear with Ring Main Unit',
      category: 'switchgear',
      link: '/projects/mv-switchgear-installation'
    },
    {
      id: 'supply-distribution-board',
      title: 'Supply of Distribution Board',
      location: 'Nairobi Industrial Area',
      image: '/images/projects/db-manufacturing-nairobi.jpg',
      year: '2024',
      scope: 'Custom Distribution Boards',
      category: 'distribution',
      link: '/projects/supply-distribution-board'
    },
    {
      id: 'control-panel-installation',
      title: 'Installation of Control Panel',
      location: 'Industrial Area, Nairobi',
      image: '/images/projects/control-panel-nairobi.jpg',
      year: '2024',
      scope: 'Automation Control Panels',
      category: 'control',
      link: '/projects/control-panel-installation'
    },
    {
      id: 'pv-grid-tie-solar',
      title: 'PV Grid Tie Solar Installation',
      location: 'Nakuru, Kenya',
      image: '/images/projects/commercial-solar-nakuru.jpg',
      year: '2025',
      scope: 'Grid-Tied Solar System with Combiner Boards',
      category: 'solar',
      link: '/projects/pv-grid-tie-solar'
    },
    {
      id: 'solar-combiner-board',
      title: 'Solar Combiner Board',
      location: 'Nairobi, Kenya',
      image: '/images/projects/solar-combiner-board.jpg',
      year: '2024',
      scope: 'Solar PV Combiner & Distribution',
      category: 'solar',
      link: '/projects/solar-combiner-board'
    },
    {
      id: 'lv-main-switchboard',
      title: 'LV Main Switchboard',
      location: 'Westlands, Nairobi',
      image: '/images/projects/lv-switchboard-nairobi.jpg',
      year: '2025',
      scope: '1600A LV Main Switchboard',
      category: 'switchgear',
      link: '/projects/lv-main-switchboard'
    },
    {
      id: 'lv-distribution',
      title: 'LV Distribution System',
      location: 'Nairobi, Kenya',
      image: '/images/projects/lv-distribution.jpg',
      year: '2024',
      scope: 'Complete LV Distribution Network',
      category: 'distribution',
      link: '/projects/lv-distribution'
    },
    {
      id: 'ring-main-unit',
      title: 'Ring Main Unit Installation',
      location: 'Nairobi, Kenya',
      image: '/images/projects/ring-main-unit.jpg',
      year: '2025',
      scope: 'RMU Switchgear Installation',
      category: 'switchgear',
      link: '/projects/ring-main-unit'
    },
    {
      id: 'ht-panel-repair-eakip',
      title: 'Repair and Maintenance of 11kV HT Panel',
      location: 'East African Kidney Institute (EAKIP)',
      image: '/images/projects/ht-panel-repair-eakip.jpg',
      year: '2025',
      scope: '11kV HT Panel Repair & Maintenance',
      category: 'switchgear',
      link: '/projects/ht-panel-repair-eakip'
    }
  ];

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'switchgear', label: 'Switchgear' },
    { id: 'distribution', label: 'Distribution' },
    { id: 'control', label: 'Control' },
    { id: 'solar', label: 'Solar' }
  ];

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.category === filter);

  return (
    <div className="pt-20">
      <section className="relative py-16 bg-gradient-to-r from-[#006D77] to-[#008B8B] text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold">Our Projects</h1>
          <p className="text-lg text-white/80 mt-2 max-w-2xl mx-auto">
            Delivering electrical solutions across Kenya
          </p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === f.id
                    ? 'bg-[#006D77] text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Link 
                key={project.id} 
                to={project.link}
                className="group bg-white rounded-xl overflow-hidden shadow hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="h-56 overflow-hidden relative">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = `https://picsum.photos/seed/${project.id}/800/600`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  
                  <div className="absolute top-3 right-3 bg-[#006D77] text-white px-2 py-1 rounded-full text-xs">
                    <Calendar size={12} className="inline mr-1" />
                    {project.year}
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <div className="flex items-center text-xs text-gray-300 mb-1">
                      <MapPin size={12} className="mr-1" />
                      {project.location}
                    </div>
                    <h3 className="font-bold text-base">{project.title}</h3>
                    <p className="text-xs text-gray-300">{project.scope}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
